/**
 * Turn a free-text search description into a structured intent.
 *
 * Users can type a combination (person + content), look up a user,
 * or just describe what they want. German and English phrasing both work.
 */

const EMAIL_TOKEN_RE = /^[^\s,;<>]+@[^\s,;<>]+$/;
const EMAIL_FIND_RE = /[^\s,;<>]+@[^\s,;<>]+/g;

const SEARCH_STOPWORDS = new Set([
  "a", "an", "the", "of", "from", "with", "about", "for", "to", "i", "want",
  "and", "or", "in", "on", "at", "all", "that", "this", "those", "these",
  "containing", "contain", "contains", "search", "find",
  "einer", "einem", "einen", "eine", "ein", "den", "der", "die", "das",
  "dem", "des", "von", "mit", "über", "ueber", "nach", "und", "oder",
  "im", "am", "auf", "aus", "bei", "allen", "suche", "suchen", "finde",
  "ich", "möchte", "moechte", "bitte",
  "email", "emails", "e-mail", "e-mails", "mail", "mails",
  "nachricht", "nachrichten", "message", "messages", "chat", "chats",
]);

function stripWrap(value) {
  return String(value || "")
    .trim()
    .replace(/^["'„“«]+/, "")
    .replace(/["'“”»]+$/, "")
    .replace(/\s+/g, " ")
    .trim();
}

function extractEmails(value) {
  const found = String(value || "").match(EMAIL_FIND_RE) || [];
  const seen = new Set();
  const out = [];
  for (const email of found) {
    const key = email.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(email);
  }
  return out;
}

function isolatePerson(person) {
  const emails = extractEmails(person);
  if (emails.length) return emails[emails.length - 1];
  return stripWrap(person);
}

/** Meaningful keywords only: emails always, other words length >= 3, no filler. */
export function contentSearchTokens(content) {
  const seen = new Set();
  const out = [];
  for (const raw of String(content || "").split(/[\s,;]+/)) {
    const word = stripWrap(raw);
    if (!word) continue;
    const key = word.toLowerCase();
    if (seen.has(key)) continue;
    if (word.includes("@")) {
      if (word.length < 3) continue;
    } else {
      if (SEARCH_STOPWORDS.has(key)) continue;
      if (word.length < 3) continue;
    }
    seen.add(key);
    out.push(word);
  }
  return out;
}

function combo(intent, person, content) {
  const p = isolatePerson(person);
  let contentStr = String(content || "");
  if (p.includes("@")) {
    const escaped = p.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    contentStr = contentStr.replace(new RegExp(escaped, "ig"), " ");
  }
  const tokens = contentSearchTokens(contentStr);
  if (p.length < 2 || !tokens.length) return null;
  const c = tokens.join(" ");
  return {
    intent,
    person: p,
    content: c,
    q: c,
    active: true,
  };
}

function description(raw, extra) {
  const q = stripWrap(raw);
  return {
    intent: extra?.intent || "all",
    person: extra?.person || "",
    content: q,
    q,
    active: false,
  };
}

/**
 * @param {string} rawSearch
 * @param {string} [explicitPerson]
 * @returns {{ intent: 'all'|'email'|'chat'|'user', person: string, content: string, q: string, active: boolean }}
 */
export function parseSearchIntent(rawSearch, explicitPerson) {
  const raw = stripWrap(rawSearch);
  const nl = parseNaturalLanguage(raw);
  // Natural language in the typed query always wins over a leftover person field.
  if (nl && nl.active) return nl;
  if (nl && nl.intent === "user") return nl;

  const explicit = stripWrap(explicitPerson);
  if (explicit) {
    const tokens = contentSearchTokens(raw);
    const intent = EMAIL_TOKEN_RE.test(explicit) ? "email" : "all";
    if (tokens.length) {
      return {
        intent,
        person: isolatePerson(explicit),
        content: tokens.join(" "),
        q: tokens.join(" "),
        active: true,
      };
    }
  }

  return nl || description(raw);
}

function parseNaturalLanguage(raw) {
  if (!raw) return null;

  const userMatch = raw.match(
    /^(?:(?:suche|search|finde|find)\s+)?(?:den\s+|die\s+)?(?:benutzer|user|person|kontakt|contact)\s*[:\-]?\s+(.+)$/i
  );
  if (userMatch) {
    const name = stripWrap(userMatch[1]);
    if (name.length >= 2) return description(name, { intent: "user", person: name });
  }
  const whoMatch = raw.match(/^(?:wer\s+ist|who\s+is)\s+(.+)$/i);
  if (whoMatch) {
    const name = stripWrap(whoMatch[1]);
    if (name.length >= 2) return description(name, { intent: "user", person: name });
  }

  // "jm@x.com in einer email von ml@x.com" / "password in an email from ml@x.com"
  const contentInEmailFrom = raw.match(
    /^(.+?)\s+in\s+(?:(?:einer|einem|einen|eine|den|der|die|das|an|the|all(?:en)?)\s+)?e-?mails?\s+(?:von|from|of)\s+(.+)$/i
  );
  if (contentInEmailFrom) {
    const parsed = combo("email", contentInEmailFrom[2], contentInEmailFrom[1]);
    if (parsed) return parsed;
  }

  // "Search Passwort in ml@x.com" / "Search jm@bl2020 in ml@x.com"
  // Content may itself be an address; the mailbox is always the one after in/bei.
  const contentInMailbox = raw.match(
    /^(.+?)\s+(?:in|bei)\s+([^\s,;]+@[^\s,;]+)\s*$/i
  );
  if (contentInMailbox) {
    const parsed = combo("email", contentInMailbox[2], contentInMailbox[1]);
    if (parsed) return parsed;
  }

  const contentInChatFrom = raw.match(
    /^(.+?)\s+in\s+(?:(?:einer|den|an|the)\s+)?(?:nachricht(?:en)?|messages?|chats?)\s+(?:von|from|of|mit|with)\s+(.+)$/i
  );
  if (contentInChatFrom) {
    const parsed = combo("chat", contentInChatFrom[2], contentInChatFrom[1]);
    if (parsed) return parsed;
  }

  const emailPatterns = [
    /^(?:(?:suche|search|finde|find)\s+)?(?:in\s+)?(?:all(?:en)?\s+)?(?:den\s+)?e-?mails?\s+(?:von|from|of)\s+(.+?)\s+(?:möchte\s+ich\s+)?(?:(?:suche?|search|finde|find)\s+(?:ich\s+)?(?:nach\s+|for\s+)?|i\s+want\s+to\s+search\s+for\s+|nach\s+|mit\s+|with\s+|über\s+|about\s+|containing\s+|die\s+enth[aä]lt\s+|that\s+contain(?:s)?\s+)(.+)$/i,
    /^(?:in\s+)?(?:all(?:en)?\s+)?e-?mails?\s+(?:von|from|of)\s+(.+?)\s*[,:–-]\s*(.+)$/i,
    /^(?:e-?mails?|mails?)\s+(?:von|from|of|an)\s+(.+?)\s+(?:mit|with|über|about|nach|containing)\s+(.+)$/i,
  ];
  for (const re of emailPatterns) {
    const m = raw.match(re);
    if (!m) continue;
    const parsed = combo("email", m[1], m[2]);
    if (parsed) return parsed;
  }

  const chatPatterns = [
    /^(?:(?:suche|search|finde|find)\s+)?(?:in\s+)?(?:den\s+)?(?:nachrichten|messages?|chats?)\s+(?:von|from|of|mit|with)\s+(.+?)\s+(?:mit|with|über|about|nach|containing|die\s+enth[aä]lt)\s+(.+)$/i,
    /^(?:nachrichten|messages?|chats?)\s+(?:von|from|of|mit|with)\s+(.+?)\s*[,:–-]\s*(.+)$/i,
  ];
  for (const re of chatPatterns) {
    const m = raw.match(re);
    if (!m) continue;
    const parsed = combo("chat", m[1], m[2]);
    if (parsed) return parsed;
  }

  const fromAbout = raw.match(
    /^(?:von|from)\s+(.+?)\s+(?:über|about|mit|with|nach)\s+(.+)$/i
  );
  if (fromAbout) {
    const intent = extractEmails(fromAbout[1]).length ? "email" : "chat";
    const parsed = combo(intent, fromAbout[1], fromAbout[2]);
    if (parsed) return parsed;
  }

  const twoEmails = twoEmailCombo(raw);
  if (twoEmails) return twoEmails;

  const commaEmail = raw.match(/^([^\s,]+@[^\s,]+)\s*,\s*(.+)$/);
  if (commaEmail && !extractEmails(commaEmail[2]).length) {
    const parsed = combo("email", commaEmail[1], commaEmail[2]);
    if (parsed) return parsed;
  }

  // "ml@x.com password" only when the rest is real keywords, not "in einer email von …"
  const leadingEmail = raw.match(/^([^\s,]+@[^\s,]+)\s+(.+)$/);
  if (leadingEmail && extractEmails(leadingEmail[2]).length === 0) {
    const rest = leadingEmail[2];
    if (
      !/\b(?:von|from|of)\b/i.test(rest) &&
      contentSearchTokens(rest).length > 0
    ) {
      const parsed = combo("email", leadingEmail[1], rest);
      if (parsed) return parsed;
    }
  }

  // One mailbox + other keywords: "Passwort ml@x.com", "Search Passwort in ml@x.com"
  const singleMailbox = singleEmailKeywordCombo(raw);
  if (singleMailbox) return singleMailbox;

  return null;
}

function singleEmailKeywordCombo(raw) {
  const emails = extractEmails(raw);
  if (emails.length !== 1) return null;
  const person = emails[0];
  const without = String(raw || "").replace(person, " ");
  const tokens = contentSearchTokens(without);
  if (!tokens.length) return null;
  return combo("email", person, tokens.join(" "));
}

function twoEmailCombo(raw) {
  const emails = extractEmails(raw);
  if (emails.length < 2) return null;
  const fromMatch = raw.match(/(?:von|from|of)\s+([^\s,;]+@[^\s,;]+)/i);
  if (fromMatch) {
    const person = isolatePerson(fromMatch[1]);
    const contentEmails = emails.filter((e) => e.toLowerCase() !== person.toLowerCase());
    if (!contentEmails.length) return null;
    return combo("email", person, contentEmails.join(" "));
  }
  const inMailbox = raw.match(/\s+(?:in|bei)\s+([^\s,;]+@[^\s,;]+)\s*$/i);
  if (inMailbox) {
    return combo("email", inMailbox[1], raw.slice(0, inMailbox.index));
  }
  const comma = raw.match(/^([^\s,]+@[^\s,]+)\s*,\s*(.+)$/);
  if (comma) return combo("email", comma[1], comma[2]);
  return null;
}

export default parseSearchIntent;
